import { match, isRight, right, left, isLeft } from '../../Either/dist/effect-Either.esm.js';
import { identity } from '../../Function/dist/effect-Function.esm.js';
import { getRight, some } from '../../Option/dist/effect-Option.esm.js';
import { flatten } from '../../ReadonlyArray/dist/effect-ReadonlyArray.esm.js';

/**
 * This module provides types and utility functions to create and work with branded types,
 * which are TypeScript types with an added type tag to prevent accidental usage of a value in the wrong context.
 *
 * The `refined` and `nominal` functions are both used to create branded types in TypeScript.
 * The main difference between them is that `refined` allows for validation of the data, while `nominal` does not.
 *
 * The `nominal` function is used to create a new branded type that has the same underlying type as the input, but with a different name.
 * This is useful when you want to distinguish between two values of the same type that have different meanings.
 * The `nominal` function does not perform any validation of the input data.
 *
 * On the other hand, the `refined` function is used to create a new branded type that has the same underlying type as the input,
 * but with a different name, and it also allows for validation of the input data.
 * The `refined` function takes a predicate that is used to validate the input data.
 * If the input data fails the validation, a `BrandErrors` is returned, which provides information about the specific validation failure.
 *
 * @since 2.0.0
 */
/**
 * @since 2.0.0
 * @category symbols
 */
const BrandTypeId = /*#__PURE__*/Symbol.for("effect/Brand");

/**
 * @since 2.0.0
 * @category symbols
 */

/**
 * @since 2.0.0
 * @category symbols
 */
const RefinedConstructorsTypeId = /*#__PURE__*/Symbol.for("effect/Brand/Refined");

/**
 * @since 2.0.0
 * @category symbols
 */

/**
 * A generic interface that defines a branded type.
 *
 * @since 2.0.0
 * @category models
 */

/**
 * @since 2.0.0
 */

/**
 * @category alias
 * @since 2.0.0
 */

/**
 * Returns a `BrandErrors` that contains a single `RefinementError`.
 *
 * @since 2.0.0
 * @category constructors
 */
const error = (message, meta) => [{
  message,
  meta
}];

/**
 * Takes a variable number of `BrandErrors` and returns a single `BrandErrors` that contains all refinement errors.
 *
 * @since 2.0.0
 * @category constructors
 */
const errors = (...errors) => flatten(errors);

/**
 * Returns a `Brand.Constructor` that can construct a branded type from an unbranded value using the provided `refinement`
 * predicate as validation of the input data.
 *
 * If you don't want to perform any validation but only distinguish between two values of the same type but with different meanings,
 * see {@link nominal}.
 *
 * @param refinement - The refinement predicate to apply to the unbranded value.
 * @param onFailure - Takes the unbranded value that did not pass the `refinement` predicate and returns a `BrandErrors`.
 *
 * @example
 * import * as Brand from "effect/Brand"
 *
 * type Int = number & Brand.Brand<"Int">
 *
 * const Int = Brand.refined<Int>(
 *   (n) => Number.isInteger(n),
 *   (n) => Brand.error(`Expected ${n} to be an integer`)
 * )
 *
 * assert.strictEqual(Int(1), 1)
 * assert.throws(() => Int(1.1))
 *
 * @since 2.0.0
 * @category constructors
 */
const refined = (refinement, onFailure) => {
  const either = args => refinement(args) ? right(args) : left(onFailure(args));
  // @ts-expect-error
  return Object.assign(args => match(either(args), {
    onLeft: e => {
      throw e;
    },
    onRight: identity
  }), {
    [RefinedConstructorsTypeId]: RefinedConstructorsTypeId,
    option: args => getRight(either(args)),
    either,
    is: args => isRight(either(args))
  });
};

/**
 * This function returns a `Brand.Constructor` that **does not apply any runtime checks**, it just returns the provided value.
 * It can be used to create nominal types that allow distinguishing between two values of the same type but with different meanings.
 *
 * If you also want to perform some validation, see {@link refined}.
 *
 * @example
 * import * as Brand from "effect/Brand"
 *
 * type UserId = number & Brand.Brand<"UserId">
 *
 * const UserId = Brand.nominal<UserId>()
 *
 * assert.strictEqual(UserId(1), 1)
 *
 * @since 2.0.0
 * @category constructors
 */
const nominal = () => {
  // @ts-expect-error
  return Object.assign(args => args, {
    [RefinedConstructorsTypeId]: RefinedConstructorsTypeId,
    option: args => some(args),
    either: args => right(args),
    is: _args => true
  });
};

/**
 * Combines two or more brands together to form a single branded type.
 * This API is useful when you want to validate that the input data passes multiple brand validators.
 *
 * @example
 * import * as Brand from "effect/Brand"
 *
 * type Int = number & Brand.Brand<"Int">
 * const Int = Brand.refined<Int>(
 *   (n) => Number.isInteger(n),
 *   (n) => Brand.error(`Expected ${n} to be an integer`)
 * )
 * type Positive = number & Brand.Brand<"Positive">
 * const Positive = Brand.refined<Positive>(
 *   (n) => n > 0,
 *   (n) => Brand.error(`Expected ${n} to be positive`)
 * )
 *
 * const PositiveInt = Brand.all(Int, Positive)
 *
 * assert.strictEqual(PositiveInt(1), 1)
 * assert.throws(() => PositiveInt(1.1))
 *
 * @since 2.0.0
 * @category combining
 */
const all = (...brands) => {
  const either = args => {
    let result = right(args);
    for (const brand of brands) {
      const nextResult = brand.either(args);
      if (isLeft(result) && isLeft(nextResult)) {
        result = left([...result.left, ...nextResult.left]);
      } else {
        result = isLeft(result) ? result : nextResult;
      }
    }
    return result;
  };
  // @ts-expect-error
  return Object.assign(args => match(either(args), {
    onLeft: e => {
      throw e;
    },
    onRight: identity
  }), {
    [RefinedConstructorsTypeId]: RefinedConstructorsTypeId,
    option: args => getRight(either(args)),
    either,
    is: args => isRight(either(args))
  });
};

export { BrandTypeId, RefinedConstructorsTypeId, all, error, errors, nominal, refined };
